import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Card,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { endpoints } from '../../../config';
import { authApiCall } from '../../../services/http';
import { setAuthUser, setAuthToken } from '../../../redux/reducers/authSlice';
import Iconify from '../../../components/Iconify';
import { MEETING_STATUS_CATALOG } from '../../../helpers/meetingStatus';

// Respuesta de GET /admin-api/kpis/activation?cohortSize=N:
//   { cohortSize, users, activated, rate, totalUsers, criteria: { minMeetings, statuses } }
// - `cohortSize` es lo PEDIDO y `users` lo DEVUELTO: pueden diferir (cohorte más pequeña que
//   el tamaño pedido), por eso el denominador que se pinta es siempre `users`.
// - `rate` viene en tanto por uno. Se usa el del back en vez de recalcular activated/users
//   para no discrepar con él si algún día cambia la fórmula.
// A diferencia del resumen de activación de la tabla —que se calcula en el cliente sobre la
// página cargada—, esta cifra la agrega el backend sobre la cohorte entera.
const COHORT_OPTIONS = [100, 250, 500, 1000];

const DEFAULT_COHORT_SIZE = 100;

const formatInt = (n) => (Number(n) || 0).toLocaleString('es-ES');

// El back manda los códigos de estado crudos (p. ej. `summary_succeed`); los traducimos con el
// catálogo compartido y caemos al código si aparece uno que no conocemos.
const statusLabel = (status) => MEETING_STATUS_CATALOG[status]?.label || status;

const criteriaText = (criteria) => {
  if (!criteria) return 'Criterio de activación definido por el backend.';
  const min = Number(criteria.minMeetings) || 1;
  const statuses = Array.isArray(criteria.statuses) ? criteria.statuses : [];
  const statusesText = statuses.length ? statuses.map(statusLabel).join(', ') : 'reunión procesada';
  return `Un usuario cuenta como activado con ${min} reunión${min === 1 ? '' : 'es'} o más en estado: ${statusesText}.`;
};

export default function ActivationCohortCard() {
  const [cohortSize, setCohortSize] = useState(DEFAULT_COHORT_SIZE);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const authToken = useSelector((state) => state.auth.authToken);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchActivation = useCallback(
    async (size, isCancelled) => {
      setLoading(true);
      setError(null);
      try {
        const res = await authApiCall({
          method: 'GET',
          endpoint: endpoints.kpisActivation,
          path: `?cohortSize=${size}`,
          navigate,
          dispatch,
          setAuthUser,
          setAuthToken,
          authToken,
          redirectRoute: null,
        });
        if (isCancelled()) return;
        setData(res?.data || null);
      } catch (e) {
        if (isCancelled()) return;
        setError(e?.response?.data?.error || e?.message || 'Error cargando la activación');
        setData(null);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    // navigate/dispatch son estables; solo el token puede cambiar entre renders.
    [authToken]
  );

  useEffect(() => {
    let cancelled = false;
    fetchActivation(cohortSize, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [cohortSize, fetchActivation]);

  // `rate` en tanto por uno → porcentaje. Con la cohorte vacía (users = 0) el back manda 0.
  const ratePct = data ? Math.round((Number(data.rate) || 0) * 100) : 0;
  const users = Number(data?.users) || 0;
  const activated = Number(data?.activated) || 0;
  // Cohorte más pequeña de lo pedido: hay que decirlo o el 63/80 con "100" seleccionado despista.
  const short = data && Number(data.cohortSize) > users;

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1">Activación</Typography>
          <Tooltip title={criteriaText(data?.criteria)}>
            <Box sx={{ display: 'flex', cursor: 'help', color: 'text.disabled' }}>
              <Iconify icon="eva:info-outline" width={18} height={18} />
            </Box>
          </Tooltip>
        </Stack>

        <TextField
          select
          size="small"
          label="Cohorte"
          value={cohortSize}
          onChange={(e) => setCohortSize(Number(e.target.value))}
          sx={{ minWidth: 160 }}
        >
          {COHORT_OPTIONS.map((size) => (
            <MenuItem key={size} value={size}>
              {formatInt(size)} usuarios
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {!error && loading && (
        <Stack spacing={1}>
          <Skeleton variant="text" width={220} height={48} />
          <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={280} />
        </Stack>
      )}

      {!error && !loading && data && (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
            <Typography variant="h3">{ratePct}%</Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{formatInt(activated)}</strong> de <strong>{formatInt(users)}</strong> han procesado una reunión
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={Math.min(ratePct, 100)}
            color="success"
            sx={{ height: 8, borderRadius: 1 }}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Typography variant="caption" color="text.disabled">
              Sobre {formatInt(data.totalUsers)} usuarios totales
            </Typography>
            {short && (
              <Typography variant="caption" color="text.disabled">
                · se pidieron {formatInt(data.cohortSize)}, la cohorte devolvió {formatInt(users)}
              </Typography>
            )}
          </Stack>
        </Stack>
      )}
    </Card>
  );
}
